import { UpdateNotifier } from '../components/feedback/update-notifier.js';
import { openNewVersionGate } from './new-version-gate.js';

let triggered = false;

/**
 * Show the blocking update gate and reload onto the current build.
 *
 * Called when the server reports the client is a stale version (a 426 response with
 * the X-App-Update-Required header). Idempotent - only the first call takes effect,
 * so many concurrent failed API requests collapse into a single gate.
 *
 * A plain reload suffices: index.html is served NetworkOnly (see public/sw.js), so
 * the reload always fetches the current shell + chunk hashes, i.e. a fresh client
 * whose version matches the server. We also nudge the service worker to pick up the
 * new build for its own caches.
 *
 * The gate opens through openNewVersionGate, as the service worker's onNeedRefresh does in
 * main.ts, so this path also stops the live-update stream for good before the gate shows (rule 1
 * of app-update-acceptance.md, D-147: whichever path shows the gate, the tab behind it holds no
 * stream).
 */
export function triggerForcedUpdate(): void {
  if (triggered) {
    return;
  }
  triggered = true;

  void navigator.serviceWorker?.getRegistration().then((reg) => reg?.update());

  // Imported here rather than at the top: the stream client reaches this module through the API
  // client (sse-client -> api factory -> agwasuite-api -> here), and a static import closes that
  // cycle, which left a class extending an undefined base when the bundle loaded. The module is
  // already loaded by the time a 426 arrives, so the gate still shows at once, after the stop.
  void import('../utility/notifications/sse-client.js').then(({ SSEClient }) =>
    openNewVersionGate({
      stopLiveUpdates: () => SSEClient.getInstance().stopForGood(),
      showGate: (onReload) => UpdateNotifier.show(onReload),
      onReload: () => {
        window.location.reload();
      },
    })
  );
}
